import { Link } from 'react-router-dom'
import { useSEO } from '@/hooks/useSEO'

const LAST_UPDATED = 'June 2026'

const h2 = 'font-display text-2xl text-archive-cream mt-10 mb-4'
const p = 'mb-4 leading-relaxed text-archive-text'
const ul = 'mb-4 list-disc space-y-2 pl-6 text-archive-text marker:text-archive-gold'
const lead = 'text-archive-cream font-medium'
const link = 'text-archive-gold hover:text-archive-amber transition-colors underline underline-offset-2'

export default function TermsPage() {
  useSEO({
    title: 'Terms of Service',
    description: 'Terms of Service for The Archive Network — the rules and guidelines for using the hub and its archives.',
    url: 'https://thearchivenetwork.com/terms',
  })

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-archive-gold">Legal</p>
          <h1 className="mt-4 font-display text-4xl text-archive-cream sm:text-5xl">Terms of Service</h1>
          <p className="mt-4 text-sm text-archive-muted">Last updated: {LAST_UPDATED}</p>
        </div>

        {/* Body */}
        <div className="mx-auto max-w-3xl">
          <h2 className={h2}>1. Acceptance of Terms</h2>
          <p className={p}>
            By accessing or using The Archive Network (the "Service") — the hub at thearchivenetwork.com and its family
            of archives — operated by Jonkai Ventures OÜ, a company registered in Estonia ("we," "us," or "our"), you
            agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the
            Service.
          </p>

          <h2 className={h2}>2. The Service</h2>
          <p className={p}>
            The Archive Network is a unified gateway to a family of documentary history and mythology archives —
            including The Civilization, Conflict, Treaty, Lineage, Origin, Classified, Exploration, Mythology, Fraud, and
            Creed Archives. The Service provides documentary, educational, and entertainment content, along with related
            features such as accounts, podcasts, and interactive tools. It is not a primary academic source.
          </p>

          <h2 className={h2}>3. Accounts &amp; Single Sign-On</h2>
          <p className={p}>
            The hub is the single sign-on (SSO) authority for the network: one account works across the hub and the
            archive sites. Sign-in is passwordless — you authenticate with your email address via a secure sign-in link
            issued by the hub, and we do not store a password. You must be at least 13 years old to create an account or
            use the Service. You are responsible for:
          </p>
          <ul className={ul}>
            <li>Maintaining control of the email account used to sign in</li>
            <li>All activity that occurs under your account</li>
            <li>Notifying us promptly of any unauthorized use of your account</li>
          </ul>

          <h2 className={h2}>4. Acceptable Use</h2>
          <p className={p}>You agree not to:</p>
          <ul className={ul}>
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to any part of the Service, other accounts, or our systems</li>
            <li>Interfere with, disrupt, or place undue load on the Service or its servers</li>
            <li>Scrape, harvest, copy, or redistribute content without our permission</li>
            <li>Use bots or other automated systems to access the Service without our consent</li>
          </ul>

          <h2 className={h2}>5. Intellectual Property</h2>
          <p className={p}>
            The Service and its original content, features, and functionality are owned by Jonkai Ventures OÜ and protected
            by international copyright, trademark, and other intellectual property laws. Content is provided for personal,
            non-commercial use unless otherwise agreed in writing.
          </p>

          <h2 className={h2}>6. Content &amp; Disclaimer</h2>
          <p className={p}>
            Network content is documentary and educational in nature and is created with the assistance of artificial
            intelligence. While we strive for accuracy, AI-assisted content may contain inaccuracies, simplifications, or
            interpretive elements, and is provided <span className={lead}>"as is"</span> for general information only. It
            is <span className={lead}>not professional, legal, medical, financial, or academic advice</span>, and we make
            no guarantee of accuracy, reliability, or completeness. For research or other critical purposes, verify
            information against primary and academic sources.
          </p>

          <h2 className={h2}>7. Interactive AI Features</h2>
          <p className={p}>
            The Service may offer interactive, AI-powered features (for example, "Speak with History" conversations with
            historical figures). These features are provided for educational and entertainment purposes, generate
            responses dynamically, and may be inaccurate, incomplete, or fictionalized. Do not rely on them as a
            statement of fact or as professional advice.
          </p>

          <h2 className={h2}>8. Disclaimer of Warranties</h2>
          <p className={p}>
            The Service is provided "as is" and "as available" without warranties of any kind, either express or implied.
            We do not warrant that the Service will be uninterrupted, secure, or error-free, or that any content will be
            accurate, reliable, or complete.
          </p>

          <h2 className={h2}>9. Limitation of Liability</h2>
          <p className={p}>
            To the fullest extent permitted by law, Jonkai Ventures OÜ shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages, or any loss of profits, revenues, data, use, goodwill, or other
            intangible losses, resulting from your use of (or inability to use) the Service.
          </p>

          <h2 className={h2}>10. Changes to These Terms</h2>
          <p className={p}>
            We may modify these terms from time to time. We will post the updated terms on this page and revise the
            "Last updated" date above. Your continued use of the Service after changes take effect constitutes acceptance
            of the modified terms.
          </p>

          <h2 className={h2}>11. Governing Law</h2>
          <p className={p}>
            These Terms are governed by and construed in accordance with Estonian law — the law of the country where
            Jonkai Ventures OÜ is registered — without regard to conflict-of-law principles. If you are a consumer
            resident in the European Union, you keep the right to bring proceedings in the courts of your country of
            residence and retain the protection of the mandatory consumer-protection provisions of your local law. For
            all other disputes, the courts of Estonia have jurisdiction.
          </p>

          <h2 className={h2}>12. Contact Us</h2>
          <p className={p}>
            If you have any questions about these Terms of Service, please reach us through our{' '}
            <Link to="/contact" className={link}>contact page</Link>.
          </p>

          {/* Footer links */}
          <div className="mt-14 flex justify-center gap-4 border-t border-archive-stone/40 pt-8 text-sm">
            <Link to="/" className={link}>Home</Link>
            <span className="text-archive-muted">|</span>
            <Link to="/privacy" className={link}>Privacy</Link>
            <span className="text-archive-muted">|</span>
            <Link to="/contact" className={link}>Contact</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
